import { css } from "styled-system/css";
import { Box, Flex, HStack, VStack } from "styled-system/jsx";
import { IconDownload, IconExternalLink, IconFileTypePdf, IconLink, IconVideo } from "@tabler/icons-react";
import { Icon, Image } from "..";
import type { Attachment } from "~/@types";

type AttachmentListProps = {
  attachments?: Attachment[];
  title?: string;
};

// Menampilkan lampiran non-galeri (PDF/tautan/video-link) sebagai kartu yang
// bisa diklik. PDF dibuka di tab baru (bisa diunduh), tautan/video → tab baru.
export const AttachmentList: React.FC<AttachmentListProps> = ({ attachments, title = "Lampiran" }) => {
  const items = (attachments ?? []).filter((a) => a && a.url);

  if (items.length === 0) return null;

  return (
    <VStack alignItems="start" gap="4" w="full">
      <h3
        className={css({
          fontSize: "18px",
          fontWeight: "bold",
          color: "secondary.darken8",
          fontFamily: "outfit!",
        })}
      >
        {title}
      </h3>
      <VStack gap="3" w="full">
        {items.map((item, idx) => (
          <AttachmentCard key={idx} item={item} />
        ))}
      </VStack>
    </VStack>
  );
};

const iconFor = (type: Attachment["type"]) => {
  if (type === "pdf") return <IconFileTypePdf />;
  if (type === "video") return <IconVideo />;
  if (type === "link") return <IconLink />;
  return <IconExternalLink />;
};

const fallbackLabel = (type: Attachment["type"]) => {
  if (type === "pdf") return "Dokumen PDF";
  if (type === "video") return "Video";
  return "Tautan";
};

const AttachmentCard: React.FC<{ item: Attachment }> = ({ item }) => {
  const isPdf = item.type === "pdf";

  // Gambar ditampilkan penuh (bukan kartu), bisa diklik untuk buka ukuran asli.
  if (item.type === "image") {
    return (
      <VStack alignItems="start" gap="1" w="full">
        <a href={item.url} target="_blank" rel="noopener noreferrer" className={css({ w: "full" })}>
          <Image
            src={item.url}
            imageStyle={{
              width: "full",
              maxH: "480px",
              objectFit: "contain",
              rounded: "12px",
              cursor: "pointer",
            }}
          />
        </a>
        {item.label ? (
          <span className={css({ textStyle: "sm", color: "secondary" })}>{item.label}</span>
        ) : null}
      </VStack>
    );
  }

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      {...(isPdf ? { download: "" } : {})}
      className={css({
        w: "full",
        textDecoration: "none",
      })}
    >
      <Flex
        alignItems="center"
        gap="3"
        w="full"
        p="4"
        rounded="12px"
        border="1px solid"
        borderColor="secondary.lighten8"
        transition="all 0.15s"
        _hover={{ borderColor: "primary", bgColor: "primary.lighten9" }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="40px"
          h="40px"
          rounded="8px"
          bgColor="primary.lighten9"
          flexShrink="0"
        >
          <Icon color="primary" size="md">
            {iconFor(item.type)}
          </Icon>
        </Box>
        <VStack alignItems="start" gap="0" flex="1" minW="0">
          <span
            className={css({
              textStyle: "md",
              fontWeight: "semibold",
              color: "secondary.darken8",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxW: "full",
            })}
          >
            {item.label || fallbackLabel(item.type)}
          </span>
          <span
            className={css({
              textStyle: "sm",
              color: "secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxW: "full",
            })}
          >
            {item.url}
          </span>
        </VStack>
        <HStack flexShrink="0">
          <Icon color="secondary" size="sm">
            {isPdf ? <IconDownload /> : <IconExternalLink />}
          </Icon>
        </HStack>
      </Flex>
    </a>
  );
};
